import { normalizeUomLabel } from '@/lib/uom'
import { sheetLineSpecAttributes } from './poSheetChain'
import type { CreatePoLinePayload } from '@/services/procurement'

export type SupplierCandidate = { id: string; name: string }

export function isSameBomBatchRun(previousSignature: string | null, currentSignature: string): boolean {
  return previousSignature !== null && previousSignature === currentSignature
}

export type SupplierAssignment = {
  supplierId: string | null
  supplierName: string | null
  candidates: SupplierCandidate[]
  requiresConfirmation: boolean
}

export function assignSupplier(
  candidates: SupplierCandidate[],
  selectedSupplierId?: string | null,
): SupplierAssignment {
  const unique = new Map<string, SupplierCandidate>()
  for (const candidate of candidates) {
    if (candidate.id && !unique.has(candidate.id)) unique.set(candidate.id, candidate)
  }
  const ordered = [...unique.values()].sort((a, b) => a.name.localeCompare(b.name, 'vi', { sensitivity: 'base' }) || a.id.localeCompare(b.id))
  if (ordered.length === 0) return { supplierId: null, supplierName: null, candidates: [], requiresConfirmation: false }
  const selected = ordered.find((candidate) => candidate.id === selectedSupplierId)
  const suggested = selected ?? ordered[0]!
  return {
    supplierId: suggested.id,
    supplierName: suggested.name,
    candidates: ordered,
    requiresConfirmation: ordered.length > 1 && !selected,
  }
}

export type BomSheetMatchInput = {
  material_id: string | null
  bom_source: 'wood' | 'hardware' | 'foam'
  material_name: string
  material_spec?: Record<string, unknown> | null
}

export function requireStandardFoamQuoteMatch<T extends {
  bom_source: string
  material_spec?: Record<string, unknown> | null
  resolution_status: 'resolved' | 'unresolved'
  resolution_notes: string | null
}>(row: T, hasMatchingQuote: boolean): T {
  if (
    row.bom_source !== 'foam' ||
    row.material_spec?.standard_foam_bom !== true ||
    row.resolution_status !== 'resolved' ||
    hasMatchingQuote
  ) return row

  return {
    ...row,
    resolution_status: 'unresolved',
    resolution_notes: 'Không có dòng báo giá khớp tên hàng và KT/Định lượng.',
  }
}

export type SheetRuleForBomMatch = {
  id: string
  item_id: string | null
  supplier_id: string
  supplier_name: string
  supplier_item_name: string
  is_sheet_backed: boolean
  mappings?: Record<string, unknown>
  input_names?: string[]
  uom?: string
  unit_price?: number
}

export type BomSheetValues = {
  values: Record<string, string | number>
  inputs: Record<string, number>
  ruleNameTokens: string[]
}

function cleanText(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value).normalize('NFC').trim()
}

function dimensionParts(value: unknown): string[] {
  const text = cleanText(value).replace(/\s*[-–—]\s*-?\d+(?:[.,]\d+)?\s*kg\s*$/i, '')
  if (!text) return []
  const parts = text.split(/\s*[x×*]\s*/i).map((part) => part.trim())
  if (parts.some((part) => !part || !/^-?\d+(?:[.,]\d+)?(?:\/-?\d+(?:[.,]\d+)?)?$/.test(part))) return []
  return parts
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  const text = cleanText(value)
  if (!/^-?\d+(?:[.,]\d+)?$/.test(text)) return null
  const result = Number(text.replace(',', '.'))
  return Number.isFinite(result) ? result : null
}

function typeCodeOf(spec: Record<string, unknown>, row: BomSheetMatchInput): string {
  const code = cleanText(spec.type_code).toLowerCase()
  if (code) return code
  const name = cleanText(row.material_name).toLocaleLowerCase('vi')
  if (row.bom_source === 'hardware') return 'hardware'
  if (name.includes('xốp l')) return 'xop_l'
  if (name.includes('xốp góc')) return 'xop_goc'
  if (name.includes('góc giấy')) return 'goc_giay'
  if (name.includes('thùng')) return 'thung'
  if (name.includes('xốp')) return 'xop_tam'
  return ''
}

function boxRuleTokens(spec: Record<string, unknown>): string[] {
  const params = spec
  const boxType = cleanText(params.box_type)
  const plyCount = asNumber(params.ply_count)
  const burst = cleanText(params.burst)
  return [boxType, plyCount === null ? '' : `${plyCount} lớp`, burst].filter(Boolean)
}

/**
 * Map canonical BOM data to the names and purchase inputs in the active quote sheet.
 * KT ordering is explicit per material type; unrecognized shapes produce no auto-match.
 */
export function bomSheetValues(row: BomSheetMatchInput): BomSheetValues {
  const spec = row.material_spec && typeof row.material_spec === 'object' ? row.material_spec : {}
  const type = typeCodeOf(spec, row)
  const values: Record<string, string | number> = {}
  const inputs: Record<string, number> = {}
  if (spec.standard_foam_bom === true) {
    const standard = spec as Record<string, unknown>
    for (const [field, source] of [['KT1', 'kt1'], ['KT2', 'kt2'], ['KT3', 'kt3'], ['KT4', 'kt4'], ['Định lượng', 'density']] as const) {
      const value = standard[source]
      if (value !== null && value !== undefined && cleanText(value)) values[field] = typeof value === 'number' ? value : cleanText(value)
    }
    return { values, inputs, ruleNameTokens: [] }
  }
  const putDimensions = (parts: Array<unknown>, names: string[]) => {
    if (parts.length !== names.length) return
    parts.forEach((part, index) => {
      const numeric = asNumber(part)
      if (numeric !== null) values[names[index]!] = numeric
    })
  }

  if (type === 'xop_l') {
    const length = asNumber(spec.length ?? spec.kt1)
    if (length !== null) {
      values.KT1 = length
      inputs.KT1 = length
    }
    putDimensions(dimensionParts(spec.cross_section ?? spec.kt2), ['KT2', 'KT3', 'KT4'])
    if (spec.density != null) values['Định lượng'] = cleanText(spec.density)
  } else if (type === 'xop_goc') {
    putDimensions(dimensionParts(spec.spec ?? spec.spec_raw ?? spec.kt1), ['KT1', 'KT2', 'KT3', 'KT4'])
    if (spec.density != null) values['Định lượng'] = cleanText(spec.density)
  } else if (type === 'xop_tam') {
    // Source spreadsheet stores KT1=length, KT2=width, KT3=thickness; quote inputs use that order.
    putDimensions([spec.length ?? spec.kt1, spec.width ?? spec.kt2, spec.thickness ?? spec.kt3], ['KT1', 'KT2', 'KT3'])
    for (const name of ['KT1', 'KT2', 'KT3']) {
      const value = asNumber(values[name])
      if (value !== null) inputs[name] = value
    }
    if (spec.density != null) values['Định lượng'] = cleanText(spec.density)
  } else if (type === 'thung') {
    putDimensions([spec.length ?? spec.kt1, spec.width ?? spec.kt2, spec.height ?? spec.kt3], ['KT1', 'KT2', 'KT3'])
    for (const name of ['KT1', 'KT2', 'KT3']) {
      const value = asNumber(values[name])
      if (value !== null) inputs[name] = value
    }
  } else if (type === 'goc_giay') {
    putDimensions(dimensionParts(spec.spec ?? spec.spec_raw ?? spec.kt1), ['KT1', 'KT2', 'KT3'])
    if (spec.density != null) values['Định lượng'] = cleanText(spec.density)
  } else if (type === 'hardware') {
    const specText = cleanText(spec.spec_raw ?? spec.spec)
    const color = cleanText(spec.color)
    if (specText) values['Quy cách'] = specText
    if (color) values.Màu = color
  }

  return { values, inputs, ruleNameTokens: type === 'thung' ? boxRuleTokens(spec) : [] }
}

function comparable(value: unknown): string {
  const text = cleanText(value)
  if (!text) return ''
  const number = asNumber(text)
  if (number !== null) return `#${number}`
  const kilograms = text.match(/^(-?\d+(?:[.,]\d+)?)\s*kg$/i)
  if (kilograms) return `#${Number(kilograms[1]!.replace(',', '.'))}`
  return normalizeUomLabel(text).normalize('NFC').replace(/\s+/g, ' ').toLocaleLowerCase('vi')
}

function ruleNameHasToken(ruleName: string, token: string): boolean {
  const compact = (value: string) => value.normalize('NFC').toLocaleLowerCase('vi').replace(/[\s\-_.]/g, '')
  return compact(ruleName).includes(compact(token))
}

export type SheetRuleMatch = { rule: SheetRuleForBomMatch; inputs: Record<string, number>; missingInputs: string[] }
export type SheetRuleMatchResult = {
  rules: SheetRuleMatch[]
  selectedRule: SheetRuleForBomMatch | null
  selectedInputs: Record<string, number>
  missingInputs: string[]
}

export function matchSheetRules(rules: SheetRuleForBomMatch[], row: BomSheetMatchInput): SheetRuleMatchResult {
  const mapped = bomSheetValues(row)
  const spec = row.material_spec && typeof row.material_spec === 'object' ? row.material_spec : {}
  const isStandardFoam = row.bom_source === 'foam' && spec.standard_foam_bom === true
  const matches = rules.flatMap((rule) => {
    if (!rule.is_sheet_backed) return []
    if (isStandardFoam) {
      if (cleanText(spec.item_name) !== cleanText(rule.supplier_item_name)) return []
    } else if (!row.material_id || rule.item_id !== row.material_id) return []
    if (mapped.ruleNameTokens.some((token) => !ruleNameHasToken(rule.supplier_item_name, token))) return []
    const purchaseInputNames = (rule.input_names ?? []).filter((name) => cleanText(name) !== 'ĐV đơn giá')
    const inputNames = new Set(purchaseInputNames)
    const entries = Object.entries(rule.mappings ?? {}).filter(([name, value]) => name !== 'ĐV đơn giá' && !inputNames.has(name) && value !== null && value !== undefined && value !== '')
    if (entries.some(([name, expected]) => {
      const actual = mapped.values[name]
      return actual === undefined || comparable(actual) !== comparable(expected)
    })) return []

    const inputs: Record<string, number> = {}
    const missingInputs: string[] = []
    for (const name of purchaseInputNames) {
      const value = mapped.inputs[name] ?? asNumber(mapped.values[name])
      if (value === null || value === undefined) missingInputs.push(name)
      else inputs[name] = value
    }
    return [{ rule, inputs, missingInputs }]
  })
  const selected = matches.length === 1 ? matches[0]! : null
  return {
    rules: matches,
    selectedRule: selected?.rule ?? null,
    selectedInputs: selected?.inputs ?? {},
    missingInputs: selected?.missingInputs ?? [],
  }
}

export type SheetPoLineInput = {
  material_id: string
  material_spec?: Record<string, unknown> | null
  spec?: string | null
  uom?: string | null
  qty_actual_buy: number
  selected_rule_id: string
  sheet_inputs: Record<string, number>
}

export function buildSheetPoLinePayload(row: SheetPoLineInput, requirementLineId: string) {
  return {
    material_id: row.material_id,
    spec: row.spec ?? null,
    spec_text: row.spec ?? null,
    spec_attributes: sheetLineSpecAttributes(row.material_spec),
    order_qty: Number(row.qty_actual_buy),
    order_uom: 'Pcs',
    selected_rule_id: row.selected_rule_id,
    sheet_inputs: { ...row.sheet_inputs },
    requirement_line_id: requirementLineId,
  }
}

export type SupplierRowGroup<T extends { supplier_id: string | null }> = { supplierId: string; rows: T[] }

export function groupRowsBySupplier<T extends { supplier_id: string | null }>(rows: T[]): SupplierRowGroup<T>[] {
  const groups = new Map<string, T[]>()
  for (const row of rows) {
    if (!row.supplier_id) continue
    const group = groups.get(row.supplier_id) ?? []
    group.push(row)
    groups.set(row.supplier_id, group)
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([supplierId, groupedRows]) => ({ supplierId, rows: groupedRows }))
}

export function remainingSupplierGroups<T extends { supplierId: string }>(
  groups: T[],
  completedSupplierIds: readonly string[],
): T[] {
  const completed = new Set(completedSupplierIds)
  return groups.filter((group) => !completed.has(group.supplierId))
}

function stableValue(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableValue).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => `${JSON.stringify(key)}:${stableValue(item)}`).join(',')}}`
  }
  return JSON.stringify(value) ?? 'undefined'
}

/** Gộp các dòng PO cùng vật tư, báo giá/đầu vào và cùng nguồn nhu cầu; giữ nguyên dòng nguồn trong batch. */
export function groupBomPoLines<T extends CreatePoLinePayload>(lines: T[]): T[] {
  const grouped = new Map<string, T>()
  for (const line of lines) {
    const key = stableValue({
      material_id: line.material_id,
      selected_rule_id: line.selected_rule_id ?? null,
      sheet_inputs: line.sheet_inputs ?? null,
      requirement_line_id: line.requirement_line_id ?? null,
      order_uom: line.order_uom,
      spec_text: line.spec_text ?? line.spec ?? null,
      spec_attributes: line.spec_attributes ?? null,
      manual_price: line.manual_price ?? null,
      manual_reason: line.manual_reason ?? null,
      price_pending: line.price_pending ?? false,
    })
    const existing = grouped.get(key)
    if (existing) existing.order_qty += line.order_qty
    else grouped.set(key, { ...line, order_qty: Number(line.order_qty), sheet_inputs: line.sheet_inputs ? { ...line.sheet_inputs } : line.sheet_inputs } as T)
  }
  return [...grouped.values()]
}
